import { EntityType } from '@frontend/common';
import { Application, Functionality } from '@frontend/functionality/types';
import { getFunctionalityDisplayName, useFunctionalityBlobImage } from '@frontend/functionality/utils';
import { ICONS, getIconByValue } from '@frontend/icons';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { isOldScreen } from './is-old-screen';

export interface ActionProps {
    application: Application | 'my';
    functionality: Functionality;
    fullWidth?: boolean;
}

export const Action = (props: ActionProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const blobImageUrl = useFunctionalityBlobImage(props.functionality);
    const Icon = props.functionality.icon ? getIconByValue(props.functionality.icon) : ICONS.APPLICATION.my;
    const SubIcon = props.functionality.sub_icon ? getIconByValue(props.functionality.sub_icon) : null;
    const [wizard, setWizard] = useState<string | null>(null);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const disabled = settingsLoaded && wizard === null && isOldScreen(props.application, props.functionality) === null;

    useEffect(() => {
        if (props.functionality.wizard_id) {
            setWizard(props.functionality.wizard_id);
            setSettingsLoaded(true);
        } else
            SettingClient.fetchSettings({ entity_id: props.functionality.id, entity_type: EntityType.FUNCTIONALITY }).then((settings) => {
                const setting = settings.results.find((s) => s.key === SettingKey.WIZARD_ID);
                if (setting && typeof setting.value === 'string') {
                    setWizard(setting.value);
                }
                setSettingsLoaded(true);
            });
    }, [props.functionality.id, props.functionality.wizard_id]);

    const onClick = () => {
        if (disabled) return;
        const oldScreen = isOldScreen(props.application, props.functionality);
        if (oldScreen) {
            navigate(oldScreen);
            return;
        } else {
            navigate(`/wizard/${props.functionality.id}/${wizard}?from=${encodeURIComponent(location.pathname + location.search)}`, { replace: false });
        }
    };

    const mainIcon = blobImageUrl ? (
        <img
            src={blobImageUrl}
            alt=''
            className='size-6 object-contain'
        />
    ) : (
        <Icon className='text-background-surface' />
    );

    if (disabled) return <></>;
    if (props.fullWidth) {
        return (
            <div className='bg-background-surface inline-flex min-w-28 items-center justify-start gap-4 self-stretch rounded-2xl p-4'>
                <div className='bg-brand-primary flex size-10 items-center justify-center gap-2.5 rounded-full'>
                    <div className='relative flex size-6 items-center justify-center overflow-hidden'>{mainIcon}</div>
                </div>
                <div className='text-foreground-default justify-start text-base font-medium'>{getFunctionalityDisplayName(intl, props.functionality)}</div>
            </div>
        );
    }

    return (
        <div
            data-testid={`${props.functionality.name.toLowerCase().replace(/\s/g, '_')}`}
            className='bg-background-surface relative flex min-w-[10rem] flex-col items-start justify-start gap-2 self-stretch rounded-2xl p-4'
            onClick={onClick}>
            <div className='size-10'>
                <div className='bg-brand-primary absolute inline-flex size-10 items-center justify-center rounded-full'>
                    <div className='relative flex size-6 items-center justify-center overflow-hidden'>{mainIcon}</div>
                </div>

                {SubIcon && (
                    <div className='bg-brand-muted absolute top-3 left-11 inline-flex size-4 items-center justify-center rounded-full'>
                        <SubIcon className='text-background-surface' />
                    </div>
                )}
            </div>
            <div className='text-foreground-default max-w-28 min-w-20 justify-end text-base font-medium'>
                {getFunctionalityDisplayName(intl, props.functionality)}
            </div>
        </div>
    );
};
